/**
 * The eight sectors (Design Bible §9.3, FS-08 R01). Content is data, not code (FS-08 R07): a sector
 * is a BPM, a palette, a hazard allow-list and a boss reference.
 *
 * The allow-lists implement FS-08 §4.4's normative **introduction schedule** — twelve kinds is a lot
 * to learn, so each sector introduces at most two new ones and sector 1 uses only the three most
 * legible. A sector may only spawn kinds in its cumulative allow-list.
 */
import type { HazardKind } from '../core/event-map';

export interface SectorPalette {
  /** Background / clear colour. */
  bg: number;
  /** Primary and secondary tunnel-shell colours. */
  primary: number;
  secondary: number;
  /** Horizon feature (the "sun"). */
  accent: number;
  /** Hull tint. */
  hull: number;
  /** Longitudinal grid lines. */
  grid: number;
  /** Base hue rotation applied to hazard colours, 0..1. */
  hazardHueShift: number;
  /** Desaturate hazards toward monochrome (Event Horizon). 0 = full colour, 1 = grey. */
  desaturate: number;
}

export interface SectorDef {
  readonly index: number; // 1-based
  readonly id: string;
  readonly name: string;
  readonly genre: string;
  readonly bpm: number;
  /** Sector 7 accelerates; when set, BPM ramps to this across the sector (Design Bible §9.3). */
  readonly bpmEnd?: number;
  readonly bossId: string;
  readonly bossName: string;
  /** Cumulative allow-list (FS-08 §4.4). */
  readonly allow: readonly HazardKind[];
  /** Seconds of survival before the Finale/boss begins. */
  readonly duration: number;
  readonly palette: SectorPalette;
  /** Custom Track's synthetic segment has no boss — the run ends when the track ends (FS-09 R26). */
  readonly noBoss?: boolean;
}

// Cumulative allow-lists, built up exactly as the introduction schedule prescribes.
const S1: HazardKind[] = ['SineRibbon', 'TriangleFence', 'StandingWave'];
const S2: HazardKind[] = [...S1, 'SquareGate', 'PulseRing'];
const S3: HazardKind[] = [...S2, 'SawtoothComb', 'FeedbackLoop'];
const S4: HazardKind[] = [...S3, 'NoiseCloud'];
const S5: HazardKind[] = [...S4, 'BeatGate', 'SubBassSlab'];
const S6: HazardKind[] = [...S5, 'HelixBraid'];
const S7: HazardKind[] = [...S6, 'ChirpSweep'];
const S8: HazardKind[] = [...S7];

export const SECTORS: readonly SectorDef[] = [
  {
    index: 1,
    id: 'sunset-boulevard',
    name: 'Sunset Boulevard',
    genre: 'Synthwave',
    bpm: 118,
    bossId: 'oscillator',
    bossName: 'THE OSCILLATOR',
    allow: S1,
    duration: 45,
    palette: {
      bg: 0x05010f,
      primary: 0x00e5ff,
      secondary: 0xff2bd6,
      accent: 0xff5da2,
      hull: 0x8cf5ff,
      grid: 0x3a2c7a,
      hazardHueShift: 0,
      desaturate: 0,
    },
  },
  {
    index: 2,
    id: 'neon-cascade',
    name: 'Neon Cascade',
    genre: 'Outrun',
    bpm: 128,
    bossId: 'ripple-cascade',
    bossName: 'RIPPLE CASCADE',
    allow: S2,
    duration: 48,
    palette: {
      bg: 0x0a0418,
      primary: 0xa46bff, // violet
      secondary: 0xffb020, // amber
      accent: 0xffc75e,
      hull: 0xd9c2ff,
      grid: 0x4a2f7d,
      hazardHueShift: 0.08,
      desaturate: 0,
    },
  },
  {
    index: 3,
    id: 'fractal-deep',
    name: 'Fractal Deep',
    genre: 'Drum & Bass',
    bpm: 140,
    bossId: 'mandelbrot-maw',
    bossName: 'MANDELBROT MAW',
    allow: S3,
    duration: 50,
    palette: {
      bg: 0x02100e,
      primary: 0x2ce8c0, // teal
      secondary: 0xa8ff3e, // lime
      accent: 0x7dffb0,
      hull: 0xc8fff0,
      grid: 0x1c5c4e,
      hazardHueShift: 0.42,
      desaturate: 0,
    },
  },
  {
    index: 4,
    id: 'static-shore',
    name: 'Static Shore',
    genre: 'Glitch Downtempo',
    bpm: 92,
    bossId: 'jammer',
    bossName: 'THE JAMMER',
    allow: S4,
    duration: 52,
    palette: {
      bg: 0x0d0a08,
      primary: 0xe8ddc8, // bone
      secondary: 0xff3b30, // red
      accent: 0xff6b5e,
      hull: 0xf5efe2,
      grid: 0x5a4a3c,
      hazardHueShift: 0.02,
      desaturate: 0.35,
    },
  },
  {
    index: 5,
    id: 'chrome-reef',
    name: 'Chrome Reef',
    genre: 'Hardstyle',
    bpm: 150,
    bossId: 'kick-drum-leviathan',
    bossName: 'KICK DRUM LEVIATHAN',
    allow: S5,
    duration: 52,
    palette: {
      bg: 0x0a0c10,
      primary: 0xd8e4f0, // chrome
      secondary: 0xff8a1f, // orange
      accent: 0xffb15e,
      hull: 0xffffff,
      grid: 0x54606e,
      hazardHueShift: 0.07,
      desaturate: 0.15,
    },
  },
  {
    index: 6,
    id: 'aurora-null',
    name: 'Aurora Null',
    genre: 'Liquid DnB',
    bpm: 174,
    bossId: 'silent-node',
    bossName: 'THE SILENT NODE',
    allow: S6,
    duration: 54,
    palette: {
      bg: 0x02100a,
      primary: 0x4dffa6, // aurora green
      secondary: 0x9affd6,
      accent: 0x6effc0,
      hull: 0xd6fff0,
      grid: 0x1d5c40,
      hazardHueShift: 0.36,
      desaturate: 0.1,
    },
  },
  {
    index: 7,
    id: 'event-horizon',
    name: 'Event Horizon',
    genre: 'Dark Techno',
    bpm: 100,
    bpmEnd: 200,
    bossId: 'gravity-well',
    bossName: 'GRAVITY WELL',
    allow: S7,
    duration: 56,
    palette: {
      bg: 0x040406,
      primary: 0xb9c0c8, // near-monochrome
      secondary: 0x7f8792,
      accent: 0xdfe6ee,
      hull: 0xffffff,
      grid: 0x3a3f46,
      hazardHueShift: 0,
      desaturate: 0.85,
    },
  },
  {
    index: 8,
    id: 'the-fundamental',
    name: 'The Fundamental',
    genre: 'Medley',
    bpm: 118,
    bossId: 'fundamental',
    bossName: 'THE FUNDAMENTAL',
    allow: S8,
    duration: 58,
    palette: {
      bg: 0x07030f,
      primary: 0x00e5ff,
      secondary: 0xff2bd6,
      accent: 0xffe45e,
      hull: 0xffffff,
      grid: 0x4a3a8a,
      hazardHueShift: 0.5,
      desaturate: 0,
    },
  },
];

/**
 * The onboarding segment's sector (FS-01 §5.2 — "Sector 1 material, Sunset Boulevard, 118 BPM").
 *
 * **Deliberately not in `SECTORS`.** Sector select, the unlock ladder, FS-08 §4.4's introduction
 * schedule check and FS-17 R32's dwell probe all iterate that list, and none of them should see
 * this: it is not a sector a player chooses, unlocks, or is expected to die in. Index 0 keeps it
 * outside the 1..8 range every one of those uses.
 */
export const ONBOARDING_SECTOR: SectorDef = {
  index: 0,
  id: 'onboarding',
  name: 'First Contact',
  genre: 'Synthwave',
  bpm: 118,
  bossId: '',
  bossName: '',
  allow: S1,
  duration: 90,
  palette: SECTORS[0]!.palette,
  noBoss: true,
};

export function sectorByIndex(i: number): SectorDef {
  if (i === 0) return ONBOARDING_SECTOR;
  return SECTORS[Math.max(0, Math.min(SECTORS.length - 1, i - 1))]!;
}

const CUSTOM_PALETTE: SectorPalette = {
  bg: 0x07030f,
  primary: 0x8a8fff,
  secondary: 0xff8fd6,
  accent: 0xd8dcff,
  hull: 0xffffff,
  grid: 0x3a3a6a,
  hazardHueShift: 0.18,
  desaturate: 0,
};

/** Custom Track's synthetic sector (FS-09 §4.1) — every kind allowed, no boss, no unlock gate. */
export function customSectorDef(bpm: number, durationSeconds: number, allow: readonly HazardKind[]): SectorDef {
  return {
    index: -1,
    id: 'custom-track',
    name: 'Custom Track',
    genre: 'Custom',
    bpm,
    bossId: '',
    bossName: '',
    allow,
    duration: durationSeconds,
    palette: CUSTOM_PALETTE,
    noBoss: true,
  };
}
